import 'virtual:uno.css'
import { Scripts, createRootRouteWithContext } from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { ConfigProvider, App as AntdApp } from 'antd'
import { isAxiosError } from 'axios'
import { getDefaultStore } from 'jotai'
import { isInitializedAtom, sessionAtom } from '@/store/auth.atom'
import { refreshAccessToken } from '@/api/refresh'
import { SessionExpiredToast } from '@/components/auth/SessionExpiredToast'

import TanStackQueryDevtools from '../integrations/tanstack-query/devtools'
import appCss from '../styles.css?url'

const ANT_THEME = {
  token: {
    colorPrimary: '#CE7A58',
    colorBgBase: '#f5f2ee',
    borderRadius: 8,
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  },
}

const AUTH_BOOTSTRAP_TIMEOUT_MS = 3000

export const Route = createRootRouteWithContext()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: import.meta.env.VITE_APP_NAME ?? 'CMS' },
    ],
    links: [{ rel: 'stylesheet', href: appCss }],
  }),
  notFoundComponent: NotFoundPage,
  beforeLoad: async () => {
    // Bootstrap runs once per full page load. Client-side navs skip this.
    // Also: skip on the server entirely — atomWithStorage reads localStorage,
    // which doesn't exist there. The browser will run this on hydration.
    if (typeof window === 'undefined') return

    const store = getDefaultStore()
    if (store.get(isInitializedAtom)) return

    const session = store.get(sessionAtom)
    if (!session) {
      store.set(isInitializedAtom, true)
      return
    }

    // Skip API verification for bypass token (dev only)
    if (session.accessToken === 'bypass-token') {
      store.set(isInitializedAtom, true)
      return
    }

    try {
      await Promise.race([
        refreshAccessToken(),
        new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Auth bootstrap timeout'))
          }, AUTH_BOOTSTRAP_TIMEOUT_MS)
        }),
      ])
    } catch (error) {
      // Only clear the session when auth is truly invalid.
      // Keep token on transient failures (network/5xx) to avoid forced logout on reload.
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status === 401 || status === 403) {
        store.set(sessionAtom, null)
      }
    } finally {
      store.set(isInitializedAtom, true)
    }
  },
  shellComponent: RootDocument,
})

function NotFoundPage() {
  return (
    <div className="flex h-screen items-center justify-center bg-[#f5f2ee] px-4">
      <div className="w-full max-w-xl rounded-2xl border border-[#e8ddd3] bg-white p-8 text-center shadow-sm">
        <p className="text-sm font-semibold tracking-wide text-[#CE7A58]">
          ERROR 404
        </p>
        <h1 className="mt-2 text-3xl font-bold text-[#3b2f2a]">
          Không tìm thấy trang
        </h1>
        <p className="mt-3 text-sm text-[#6e625c]">
          Đường dẫn bạn truy cập không tồn tại hoặc đã được thay đổi.
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            className="rounded-lg bg-[#CE7A58] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            onClick={() => {
              window.location.assign('/')
            }}
          >
            Về trang chủ
          </button>
          <button
            type="button"
            className="rounded-lg border border-[#d8ccc2] px-4 py-2 text-sm font-semibold text-[#5e524c] hover:bg-[#f7f3ef]"
            onClick={() => {
              if (window.history.length > 1) {
                window.history.back()
                return
              }
              window.location.assign('/')
            }}
          >
            Quay lại
          </button>
        </div>
      </div>
    </div>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <ConfigProvider theme={ANT_THEME}>
      <AntdApp>
        <div className="hide-scrollbar full-height">{children}</div>
        <SessionExpiredToast />
      </AntdApp>
      <TanStackDevtools
        config={{ position: 'bottom-right' }}
        plugins={[
          {
            name: 'Tanstack Router',
            render: <TanStackRouterDevtoolsPanel />,
          },
          TanStackQueryDevtools,
        ]}
      />
      <Scripts />
    </ConfigProvider>
  )
}
